import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { NavigationEnd, Router } from '@angular/router';
import { BaselineRequest, BulkImportType } from '@dr-customer-offers-ui/lib-interfaces';
import { Actions as EffectActions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BulkType,
  DataModel,
  DetailTableCellModel,
  NgxIntervalDataGridRowModel,
  NgxIntervalDataGridService,
  TableDataTypes,
  Week,
  WeekDays
} from '@ngx-interval-data-grid';
import moment from 'moment';
import { filter, Observable, of } from 'rxjs';
import { BulkInput, InputTypeEnum, OffersDataInterface, TabOption } from '../../../shared/models';
import { BusinessRole } from '../../../shared/models/role';
import { UIState } from '../../../shared/models/utility';
import { DataModelService } from '../../../shared/services/data-model.service';
import { DataViewModelService } from '../../../shared/services/data-vm.service';
import { DateService } from '../../../shared/services/date.service';
import { InternalService } from '../../../shared/services/internal.service';
import { MixPanelService } from '../../../shared/services/mixpanel.service';
import { AppState } from '../../../store';
import * as Actions from '../../../store/actions/com.actions';
import * as Selectors from '../../../store/selectors/com.selectors';
import { FiltersStateService } from '../../filters/filters-state.service';

@Component({
  selector: 'dr-customer-offers-ui-baselines-table',
  templateUrl: './baselines-tab-table.component.html',
  styleUrls: ['./baselines-tab-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaselinesTabTableComponent implements OnInit {
  protected baselineOffersLoading = this.store.selectSignal(Selectors.selectBaselineOffersLoading);
  protected currentRegViewConfig = this.store.selectSignal(Selectors.selectCurrentRegViewConfig);

  protected UIState = UIState;
  protected baselinesData: NgxIntervalDataGridRowModel[] | null = null;
  protected dates: number[] = [];
  protected timezoneAbbr!: string;

  // Define column headers and fields
  protected columns: string[] = WeekDays;
  protected displayedColumns: string[] = ['offer', 'time_period', ...this.columns];
  protected serverData!: NgxIntervalDataGridRowModel[];
  protected dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
  protected minDate!: moment.Moment;
  protected maxDate!: moment.Moment;
  protected editModeSignal: WritableSignal<boolean> = signal(false);
  protected showDownloadUploadOptionsSignal = signal(false);
  protected disableConfirmButton$: Observable<boolean> = this.ngxService.getDisableSave();
  protected applyButtonDisabled = this.filtersStateService.isApplyButtonDisabledSignal;
  private selectedRegControl: FormControl = this.dataVMService.selectedRegFormControl;
  private selectedDateRangeControl: FormControl = this.dataVMService.selectedFromWeekPickerFormControl;
  protected displaySelectedReg = '';
  private userHasIrishMarketUserRole = this.store.selectSignal(Selectors.selectHasBusinessRole(BusinessRole.IrishMarketUser));
  private userHasCustomerOfferUserRole = this.store.selectSignal(Selectors.selectHasBusinessRole(BusinessRole.CustomerOfferUser));
  protected showEditButton = computed(() => {
    if (!this.dataVMService.externalUserSignal()) return true;
    return this.userHasIrishMarketUserRole() && this.userHasCustomerOfferUserRole();
  });
  protected isBulkImportAllowed = computed(() => {
    return this.showEditButton() && !!this.currentRegViewConfig()?.allow_bulk_upload && !this.editModeSignal();
  });
  protected dateLabel$: Observable<string | null> = of(null);
  private userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');

  constructor(
    private dataModelService: DataModelService,
    private dataVMService: DataViewModelService,
    private ngxService: NgxIntervalDataGridService,
    private internalService: InternalService,
    private cdr: ChangeDetectorRef,
    private mixPanelService: MixPanelService,
    private router: Router,
    private destroyRef: DestroyRef,
    private store: Store<AppState>,
    private filtersStateService: FiltersStateService,
    private dateService: DateService,
    private actions$: EffectActions
  ) {}

  ngOnInit(): void {
    this.actions$.pipe(ofType(Actions.bulkImportCompleted), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadData());

    this.store
      .select(Selectors.selectCurrentBaselineOffers)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((offers) => offers !== null)
      )
      .subscribe((offers: OffersDataInterface | null) => {
        if (offers) this.processGroupedData(offers);
        this.filtersStateService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
      });

    this.loadData();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.loadData();
      });

    this.selectedDateRangeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.mixPanelService.viewTab('Baselines');
    this.applyBulk();
  }

  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  triggerEditMode(isEditMode: boolean): void {
    this.editModeSignal.set(isEditMode);
  }

  private loadData(): void {
    const selectedDates = this.dataVMService.getAppliedWeekRange();
    const regConfig = this.currentRegViewConfig();

    if (!regConfig || !selectedDates || !selectedDates.start || !selectedDates.end || this.selectedRegControl.value === null) {
      console.warn('Missing required data for loading offers');
      return;
    }

    // We need this setTimeOut to make sure we do not dispatch loadBaselineOffers with previous filter values especially start and end date.
    setTimeout(() => {
      this.store.dispatch(Actions.loadBaselineOffers());
    }, 10);

    this.displaySelectedReg = regConfig.display_labels || '';
  }

  /**
   * This method is updated each time you enter a value on any of the input field
   * @param element provides the whole element which is being updated
   * @param event is an KeyboardEvent where we get the value entered.
   * @param week is used to provide the week which we are updating the value for.
   */
  inputToCellValue(element: NgxIntervalDataGridRowModel, inputEvent: KeyboardEvent, week: string) {
    const target = inputEvent?.target as HTMLInputElement | null;
    const value: number | null = target?.valueAsNumber === undefined || isNaN(target.valueAsNumber) ? null : target?.valueAsNumber;
    const updatedDataElement = this.ngxService.updateElementValue({
      weekName: week as Week,
      timePeriodKey: element.timePeriodKey,
      value,
      invalid_value: false,
      tableDataType: TableDataTypes.BASELINE
    });
    this.serverData = [...updatedDataElement];
  }

  /**
   * This method is used when user cancels from edit mode. Here we forget all his previous updates and take data we recieved from the server.
   */
  cancel() {
    const data: DataModel[] = this.ngxService.getUntouchedDataFromServer().dataModelRef[TableDataTypes.BASELINE] as DataModel[];
    this.ngxService.clearUnSavedData();
    this.editModeSignal.set(false);
    this.dataSource.data = this.ngxService.groupByDayOfWeek(data, this.currentRegViewConfig()?.timeZone ?? 'UTC', TableDataTypes.BASELINE);
  }

  /**
   * This is the main method used to save the updated data and switch back to the readonly mode
   */

  save() {
    const unsavedOffers: DetailTableCellModel[] = this.dataVMService.lookForDSTDuplication(
      this.ngxService.unsavedData,
      this.currentRegViewConfig()?.intervalFrequency
    );
    const baselineToPost: BaselineRequest[] = this.dataModelService.getBaselineToPost(unsavedOffers, this.baselinesData, this.currentRegViewConfig());
    this.postBaseline(baselineToPost);
  }

  postBaseline(baseline: BaselineRequest[]) {
    const selectedDates = this.dataVMService.getAppliedWeekRange();
    const regConfig = this.currentRegViewConfig();

    if (regConfig && selectedDates) {
      const selectedDatesISO = this.dateService.getISODate(selectedDates, regConfig.timeZone);
      this.store.dispatch(
        Actions.postBaselineOffers({
          offers: baseline,
          registrationId: regConfig.registrationId,
          startDate: selectedDatesISO.start,
          endDate: selectedDatesISO.end
        })
      );
    }
  }

  applyBulk(): void {
    this.internalService.getBulkInputs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((b: BulkInput | null) => {
      if (!b) return;
      switch (b.bulkType) {
        case BulkType.CUSTOM:
          {
            if (b.inputType === InputTypeEnum.VALUE && b.value !== null && b.value !== undefined) {
              this.serverData = [
                ...this.ngxService.updateValueBulk(
                  BulkType.CUSTOM,
                  b.value,
                  TableDataTypes.BASELINE,
                  undefined,
                  moment(b.startDateAndTime),
                  moment(b.endDateAndTime),
                  false,
                  b.operation
                )
              ];
            }
          }
          break;
        case BulkType.DAY:
          if (b.value !== null && b.value !== undefined)
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.DAY, b.value, TableDataTypes.BASELINE, b.week)];
          break;
        case BulkType.WEEK:
          if (b.value !== null && b.value !== undefined)
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.WEEK, b.value, TableDataTypes.BASELINE)];
          break;
      }
      this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
      this.cdr.detectChanges();
    });
  }

  private processGroupedData(value: OffersDataInterface): void {
    const timeZone = this.currentRegViewConfig()?.timeZone ?? 'UTC';

    this.timezoneAbbr = moment.tz(timeZone).zoneName();
    this.dataSource.data = this.baselinesData = value.values ?? [];

    const startDate = moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone);
    this.dates = [];
    this.displayedColumns = value.values?.[0].groupedOffersIntervals ? ['time_period', ...this.columns] : ['offer', 'time_period', ...this.columns];

    for (let i = 0; i < 7; i++) {
      const newDate = startDate.clone().add(i, 'days');
      this.dates.push(newDate.date());
    }

    const start = moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    const end = moment(this.dataSource.data[0].Saturday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    this.dateLabel$ = of(`${start} - ${end}`);

    this.editModeSignal.set(false);
    this.cdr.detectChanges();
  }

  toggleDownloadUploadOptions(event: MouseEvent): void {
    event.stopPropagation();
    this.showDownloadUploadOptionsSignal.update((show) => !show);
  }

  clickOutsideDownloadUploadOptions(): void {
    this.showDownloadUploadOptionsSignal.update((show) => (show ? false : show));
  }

  openBulkUploadFunctionalityDialog(): void {
    const regConfig = this.currentRegViewConfig();
    this.dataVMService.openBulkUploadFunctionalityDialog({
      minDate: this.minDate,
      maxDate: this.maxDate,
      registrationId: regConfig?.registrationId ?? '',
      intervalFrequency: regConfig?.intervalFrequency ?? 60,
      timeZone: regConfig?.timeZone ?? 'UTC',
      importType: BulkImportType.Baselines,
      registrationName: regConfig?.display_labels ?? ''
    });
  }
}
